import type { DateFormat, TimeFormat, UserPreferences, WeekStart } from "@orsane/schema/users";
import { type Day, differenceInHours, format, formatDistance, formatRelative, type Locale } from "date-fns";
import { enUS } from "date-fns/locale";

/*
 * Date and time display follows the user's preferences (Settings -> Preferences). Don't
 * call date-fns `format` with a hardcoded pattern for anything a user reads: go through
 * `useDateFormatter()` (src/hooks/useUserPreferences.ts), which binds these to the
 * current user. Machine formats such as the "yyyy-MM-dd" sent to the API stay as they are.
 */

/** The same notation without the year, for compact lists (inbox, timer entries). */
const SHORT_DATE_PATTERNS: Record<DateFormat, string> = {
    "MMM d, yyyy": "MMM d",
    "d MMM yyyy": "d MMM",
    "MM/dd/yyyy": "MM/dd",
    "dd/MM/yyyy": "dd/MM",
    "dd.MM.yyyy": "dd.MM.",
    "yyyy-MM-dd": "MM-dd",
};

const TIME_PATTERNS: Record<TimeFormat, string> = {
    "12h": "h:mm a",
    "24h": "HH:mm",
};

export const WEEK_STARTS_ON: Record<WeekStart, Day> = {
    sunday: 0,
    monday: 1,
    saturday: 6,
};

/**
 * en-US, with `formatRelative`'s "at 3:04 PM" and "03/05/2026" swapped for the user's time
 * and date patterns, so relative dates don't bypass the preference.
 */
function relativeLocale(datePattern: string, timePattern: string): Locale {
    const tokens: Record<string, string> = {
        lastWeek: `'last' eeee 'at' ${timePattern}`,
        yesterday: `'yesterday at' ${timePattern}`,
        today: `'today at' ${timePattern}`,
        tomorrow: `'tomorrow at' ${timePattern}`,
        nextWeek: `eeee 'at' ${timePattern}`,
        other: datePattern,
    };
    return { ...enUS, formatRelative: (token) => tokens[token] ?? datePattern };
}

export type DateFormatter = ReturnType<typeof createDateFormatter>;

/** Formatting functions bound to one user's preferences. */
export function createDateFormatter({ dateFormat, timeFormat, weekStart }: UserPreferences) {
    const timePattern = TIME_PATTERNS[timeFormat];
    const weekStartsOn = WEEK_STARTS_ON[weekStart];
    const locale = relativeLocale(dateFormat, timePattern);

    return {
        weekStartsOn,
        /** "Mar 5, 2026" */
        formatDate: (date: Date) => format(date, dateFormat),
        /** "Mar 5" */
        formatShortDate: (date: Date) => format(date, SHORT_DATE_PATTERNS[dateFormat]),
        /** "3:04 PM" / "15:04" */
        formatTime: (date: Date) => format(date, timePattern),
        /** "Mar 5, 2026 3:04 PM" */
        formatDateTime: (date: Date) => format(date, `${dateFormat} ${timePattern}`),
        formatRelativeDate: (date: Date) => {
            // if less than 4 hours, use formatDistance
            if (differenceInHours(date, new Date()) < 4) {
                return formatDistance(date, new Date(), {
                    addSuffix: true,
                    includeSeconds: false,
                });
            }
            return formatRelative(date, new Date(), { weekStartsOn, locale });
        },
    };
}

// Convert minutes to optimized duration string
export function formatDuration(totalMinutes: number | null): string {
    if (totalMinutes === null) return "";

    if (totalMinutes === 0) return "0m";

    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    const parts: string[] = [];

    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.join(" ");
}

/** Same shape as formatDuration, but from seconds. Sub-minute time reads as "<1m". */
export function formatDurationSeconds(totalSeconds: number | null): string {
    if (totalSeconds === null) return "";
    if (totalSeconds === 0) return "0m";
    if (totalSeconds < 60) return "<1m";
    return formatDuration(Math.floor(totalSeconds / 60));
}

/**
 * Full precision, for a timer that is currently running: "1d 4h 3m 4s". Seconds are
 * always shown, since the point of a live readout is that it moves.
 */
export function formatDurationWithSeconds(totalSeconds: number): string {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const days = Math.floor(safe / 86400);
    const hours = Math.floor((safe % 86400) / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const seconds = safe % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);

    return parts.join(" ");
}

/**
 * A stopwatch reading: "0:25:00", "10:00:20". Hours accumulate rather than rolling into
 * days — a clock that resets at 24h would misread a long-running total. Used for the
 * individual entries in the timer panel; the panel's *total* keeps the duration shape,
 * which is easier to scan than a six-digit clock.
 */
export function formatClock(totalSeconds: number): string {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const seconds = safe % 60;

    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
